import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const MOUSE_GRACE_MS = 1000;
const NATIVE_DIALOGS = ['alert', 'confirm', 'prompt'];

// Detects the learner leaving the page (tab, window/app, mouse) and records one strike per time away
export const useAwayDetection = ({ enabled, mouseLeave, limit, initialStrikes, apiRef, attemptRef, onLimit }) => {
    const [away, setAway] = useState(false);
    const [strikes, setStrikes] = useState(initialStrikes);
    const [signingOut, setSigningOut] = useState(false);
    const strikesRef = useRef(initialStrikes);

    useEffect(() => { strikesRef.current = initialStrikes; setStrikes(initialStrikes); }, [initialStrikes]);

    useEffect(() => {
        if (!enabled) return undefined;
        const s = { away: false, counted: false, timer: null, mouseOut: false, ignoreUntil: 0, ending: false };
        const ignoring = () => Date.now() < s.ignoreUntil;
        const ignoreFor = (ms) => { s.ignoreUntil = Date.now() + ms; };

        const strike = async (kind) => {
            if (s.counted || s.ending) return;
            s.counted = true;
            let res = null;
            try {
                res = (await apiRef.current.post('/focus-guard/strike', { kind, page: window.location.pathname, attempt: attemptRef.current?.label || null })).data;
            } catch { /* offline: fall back to the local count */ }
            if (res?.ignored) return;
            const n = res ? res.strikes : strikesRef.current + 1;
            strikesRef.current = n;
            setStrikes(Math.min(n, limit));
            if (res ? res.logout : n >= limit) {
                s.ending = true;
                setSigningOut(true);
                onLimit(limit);
            }
        };
        const leave = (kind) => {
            if (s.ending || ignoring()) return;
            if (!s.away) { s.away = true; s.counted = false; setAway(true); }
            clearTimeout(s.timer);
            if (kind === 'mouse_leave') s.timer = setTimeout(() => strike(kind), MOUSE_GRACE_MS);
            else strike(kind);
        };
        const back = () => {
            if (!s.away || s.ending) return;
            if (document.visibilityState !== 'visible' || !document.hasFocus() || (mouseLeave && s.mouseOut)) return;
            clearTimeout(s.timer);
            s.away = false;
            setAway(false);
            if (s.counted) toast.warning(`Strike ${Math.min(strikesRef.current, limit)} of ${limit}: stay on this page while you train.`, { duration: 5000 });
        };

        const onVisibility = () => (document.visibilityState === 'hidden' ? leave('tab_switch') : back());
        const onBlur = () => setTimeout(() => {
            if (document.visibilityState === 'hidden' || document.hasFocus() || ignoring()) return;
            leave('window_blur');
        }, 0);
        const onMouseOut = (e) => {
            if (!mouseLeave || e.relatedTarget || ignoring()) return;
            s.mouseOut = true;
            leave('mouse_leave');
        };
        const onMouseIn = () => { if (s.mouseOut) { s.mouseOut = false; back(); } };
        const onClick = (e) => { if (e.target?.matches?.('input[type="file"]')) ignoreFor(60000); };
        const onFocus = () => { if (s.ignoreUntil > Date.now() + 1500) s.ignoreUntil = Date.now() + 500; back(); };
        const onFullscreen = () => ignoreFor(1200);

        const originals = NATIVE_DIALOGS.map((name) => window[name]);
        NATIVE_DIALOGS.forEach((name, i) => {
            window[name] = (...args) => { ignoreFor(60000); try { return originals[i].apply(window, args); } finally { ignoreFor(400); } };
        });
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', onFocus);
        document.addEventListener('mouseout', onMouseOut);
        document.addEventListener('mouseover', onMouseIn);
        document.addEventListener('mousemove', onMouseIn);
        document.addEventListener('click', onClick, true);
        document.addEventListener('fullscreenchange', onFullscreen);
        return () => {
            clearTimeout(s.timer);
            NATIVE_DIALOGS.forEach((name, i) => { window[name] = originals[i]; });
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('mouseout', onMouseOut);
            document.removeEventListener('mouseover', onMouseIn);
            document.removeEventListener('mousemove', onMouseIn);
            document.removeEventListener('click', onClick, true);
            document.removeEventListener('fullscreenchange', onFullscreen);
            setAway(false);
        };
    }, [enabled, mouseLeave, limit, apiRef, attemptRef, onLimit]);

    return { away, strikes, signingOut };
};
